/**
 * RecentlyViewedCard — Dashboard tile with the last 5 shifts, medics,
 * contracts or templates the current user opened. One-click return to
 * a record in progress (iter72.5).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, Calendar, Users, FileText, Layers, ArrowRight } from 'lucide-react';
import { formatDistanceToNowStrict } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';

const ICONS = {
  shift: Calendar,
  medic: Users,
  contract: FileText,
  template: Layers,
};

const TYPE_LABELS = {
  shift: 'Shift',
  medic: 'Medic',
  contract: 'Contract',
  template: 'Template',
};

export function RecentlyViewedCard() {
  const { items, clear } = useRecentlyViewed();

  if (!items || items.length === 0) return null;

  return (
    <Card className="bg-[#2B2B2B] border-white/10" data-testid="recently-viewed-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="font-['Manrope'] text-white flex items-center gap-2 text-base">
          <Clock className="w-4 h-4 text-[#1ABC9C]" />
          Recently viewed
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={clear}
          className="text-xs text-[#A1A1AA] hover:text-white hover:bg-white/5 h-7 px-2"
          data-testid="recently-viewed-clear"
        >
          Clear
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        <ul className="divide-y divide-white/5">
          {items.map((item) => {
            const Icon = ICONS[item.type] || Clock;
            return (
              <li key={`${item.type}:${item.id}`}>
                <Link
                  to={item.href}
                  className="flex items-center gap-3 py-2.5 group hover:bg-white/5 -mx-2 px-2 rounded-md transition-colors"
                  data-testid={`recent-${item.type}-${item.id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-[#1ABC9C]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#1ABC9C]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate font-medium">{item.label}</div>
                    <div className="text-xs text-[#A1A1AA] flex items-center gap-2">
                      <span>{TYPE_LABELS[item.type] || item.type}</span>
                      {item.subtitle && <><span>·</span><span className="truncate">{item.subtitle}</span></>}
                      <span>·</span>
                      <span>{formatDistanceToNowStrict(new Date(item.ts), { addSuffix: true })}</span>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#52525B] group-hover:text-[#1ABC9C] transition-colors flex-shrink-0" />
                </Link>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

export default RecentlyViewedCard;
